import { URL_STATUS_LABELS } from './accuracyEvaluationScope';
import { getReportGuidelineRefs, guidelineRefKey } from './accuracyGuidelineLabels';
import { formatGuidelineWithVersion, type AccuracyScopeMetadata } from './accuracyScopeMetadata';
import type { SavedAccuracyReport, UrlStatus } from '../types/accuracy';

export interface AccuracyReportListFilterState {
  /** `${guideline_id}:${version}` */
  guidelineKeys: string[];
  jobIds: string[];
  urlStatuses: UrlStatus[];
  /** YYYY-MM-DD */
  createdFrom: string;
  /** YYYY-MM-DD */
  createdTo: string;
}

export const EMPTY_REPORT_LIST_FILTER: AccuracyReportListFilterState = {
  guidelineKeys: [],
  jobIds: [],
  urlStatuses: [],
  createdFrom: '',
  createdTo: '',
};

export function buildGuidelineFilterOptions(
  reports: SavedAccuracyReport[],
  metadata?: AccuracyScopeMetadata | null
) {
  const options = new Map<string, { label: string; value: string }>();

  for (const report of reports) {
    for (const ref of getReportGuidelineRefs(report, metadata)) {
      const key = guidelineRefKey(ref);
      if (!options.has(key)) {
        options.set(key, {
          label: formatGuidelineWithVersion(ref, metadata),
          value: key,
        });
      }
    }
  }

  return Array.from(options.values()).sort((a, b) => b.label.localeCompare(a.label, 'ja'));
}

export function buildJobFilterOptions(metadata?: AccuracyScopeMetadata | null) {
  if (metadata && metadata.jobsById.size > 0) {
    return Array.from(metadata.jobsById.values())
      .map((j) => ({
        label: j.job_name,
        value: j.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  }
  return [];
}

export function buildUrlStatusFilterOptions(reports: SavedAccuracyReport[]) {
  const statuses = new Set<UrlStatus>();
  for (const r of reports) {
    for (const s of r.filters.url_statuses) statuses.add(s);
  }
  const ordered = (Object.keys(URL_STATUS_LABELS) as UrlStatus[]).filter((s) =>
    statuses.has(s)
  );
  return ordered.map((s) => ({ label: URL_STATUS_LABELS[s], value: s }));
}

export function filterSavedReports(
  reports: SavedAccuracyReport[],
  state: AccuracyReportListFilterState
): SavedAccuracyReport[] {
  return reports.filter((report) => {
    if (state.guidelineKeys.length > 0) {
      const reportKeys = new Set(getReportGuidelineRefs(report).map(guidelineRefKey));
      const matchesGuideline = state.guidelineKeys.some((k) => reportKeys.has(k));
      if (!matchesGuideline) return false;
    }

    if (state.jobIds.length > 0) {
      const reportJobIds = report.filters.job_ids;
      const matchesJob = state.jobIds.some((id) => reportJobIds.includes(id));
      if (!matchesJob) return false;
    }

    if (state.urlStatuses.length > 0) {
      const matchesStatus = state.urlStatuses.some((s) =>
        report.filters.url_statuses.includes(s)
      );
      if (!matchesStatus) return false;
    }

    const createdMs = new Date(report.created_at).getTime();
    if (state.createdFrom) {
      const fromMs = new Date(`${state.createdFrom}T00:00:00`).getTime();
      if (createdMs < fromMs) return false;
    }
    if (state.createdTo) {
      const toMs = new Date(`${state.createdTo}T23:59:59.999`).getTime();
      if (createdMs > toMs) return false;
    }

    return true;
  });
}

export function hasActiveReportListFilters(state: AccuracyReportListFilterState): boolean {
  return (
    state.guidelineKeys.length > 0 ||
    state.jobIds.length > 0 ||
    state.urlStatuses.length > 0 ||
    Boolean(state.createdFrom) ||
    Boolean(state.createdTo)
  );
}
